import React from 'react'
import { connect } from 'react-redux'
import { useLocation } from 'react-router'
import PropTypes from 'prop-types'
import { ROLES } from 'config/constants'
import ContentEntryDataGetter from 'layers/content/Hocs/ContentEntryDataGetter'
import { ERROR_TYPE_CONTENTFUL } from 'layers/errorHandling/apiError'
import withApiError from 'layers/errorHandling/apiError/component'
import NavigationHelper from 'layers/navigation/navigationHelper'
import { navigationEvent } from 'layers/navigation/store/operations'
import {
  selectors as licenseSelectors,
  operations as licenseOperations,
} from 'store/licenseManager'
import siteOperations from 'store/siteManager/operations'
import { operations as userSessionOperations } from 'store/userSessionManager'
import { TOGGLE_VIEW } from 'store/userSessionManager/constants'
import { TABLE_URL_PARAMS } from 'store/reportsManager/constants'
import AdminDashboardBanner from './component'
import { findProgramLinks } from 'utils/findProgramLinks'
import { Logger } from 'logging'
import { getMSOptInData } from 'utils/siteData'

export const AdminDashboardBannerContainer = ({
  activeAdminHsSites,
  activeAdminK8Sites,
  claims,
  displayTitle,
  // fetchSetupLicensesForUser,
  // isFetchingSetupLicenses,
  isLaunched,
  isSetupAdmin,
  hsToggleView,
  isSingleK8Site,
  k8ToggleView,
  mspSites,
  navigationEvent,
  onChangeProgram,
  onboardingSnapshot,
  programQuickLinks,
  programTitles,
  recentLeaderDashboardUrl,
  selectedProgram,
  setHSToggleView,
  setK8ToggleView,
  setLeaderDashboardRecentUrl,
  unassignedLicenses,
  updateMSPOptIn,
}) => {
  // Determine which sites to use based on selected program, not page type
  const getSitesForProgram = program => {
    if (!program) return []

    switch (program.key) {
      case 'HS':
        return activeAdminHsSites || []
      case 'K8':
        return activeAdminK8Sites || []
      case 'SELA':
      case 'BPU':
      case 'CPU':
        return []
      default:
        return []
    }
  }

  const sitesForSelectedProgram = getSitesForProgram(selectedProgram)

  // useEffect(() => { <--- Bring back with https://secondstep.atlassian.net/browse/LEARN-24951
  //   if (isSetupAdmin && !isFetchingSetupLicenses && !unassignedLicenses)
  //     fetchSetupLicensesForUser()
  // }, [])

  const location = useLocation()
  const isUserProgressPage = location?.pathname?.includes('/user-progress')
  const isSmallGroupLessonCompletionPage = location?.pathname?.includes(
    '/small-group-lesson-completion',
  )

  const getAvailableLinks = () => {
    if (!programQuickLinks || !selectedProgram) {
      return []
    }

    const {
      hasBpuAdminClaim,
      hasCpuAdminClaim,
      hasElemAdminClaim,
      hasHighschoolAdminClaim,
      hasMsAdminClaim,
      hasSelaAdminClaim,
    } = claims

    const programAccessMap = {
      BPU: { access: hasBpuAdminClaim, program: 'bpuDigital' },
      CPU: { access: hasCpuAdminClaim, program: 'cpuDigital' },
      HS: { access: hasHighschoolAdminClaim, program: 'highSchool' },
      K8: {
        access: hasElemAdminClaim && hasMsAdminClaim,
        k5program: 'k5SelDigital',
        msprogram: 'mspDigital',
      },
      SELA: { access: hasSelaAdminClaim, program: 'sela' },
    }

    return programAccessMap?.[selectedProgram.name]?.access
      ? findProgramLinks(programQuickLinks, selectedProgram, programAccessMap)
      : []
  }

  const handleKeyPress = (e, viewOrUrl) => {
    if (e.key === 'Enter') {
      if (
        viewOrUrl === TOGGLE_VIEW.PREPARE ||
        viewOrUrl === TOGGLE_VIEW.LAUNCH ||
        viewOrUrl === TOGGLE_VIEW.MONITOR
      ) {
        handleTabChange(viewOrUrl)
      } else {
        navigateToSameTab(viewOrUrl)
      }
    }
  }

  const handleTabChange = (view, siteId) => {
    if (
      view === TOGGLE_VIEW.PREPARE ||
      view === TOGGLE_VIEW.LAUNCH ||
      view === TOGGLE_VIEW.MONITOR ||
      view === TOGGLE_VIEW.INTERVENTIONS
    ) {
      const views = {
        [TOGGLE_VIEW.PREPARE]: 'prepare',
        [TOGGLE_VIEW.LAUNCH]: 'program_adoption',
        [TOGGLE_VIEW.MONITOR]: 'monitor',
        [TOGGLE_VIEW.INTERVENTIONS]: 'interventions',
      }
      let pagedViewUrl = `/admin/program/${selectedProgram.key.toLowerCase()}/`

      if (selectedProgram.key === 'K8') {
        setLeaderDashboardRecentUrl(`${pagedViewUrl.slice(1)}${views[view]}`)
        setK8ToggleView(view)
      }
      if (selectedProgram.key === 'HS') {
        setLeaderDashboardRecentUrl(`${pagedViewUrl.slice(1)}${views[view]}`)
        setHSToggleView(view)
      }

      // Use absolute path instead of relative path to prevent duplicate URL segments
      const absoluteUrl = `${pagedViewUrl}${views[view]}`
      navigationEvent(absoluteUrl, 'REPLACE')

      try {
        pagedViewUrl += views[view]
        Logger.logEvent({
          name: pagedViewUrl,
          properties: { viewOption: views[view] },
        })
      } catch (e) {
        console.error('Cannot log Admin toggle event')
      }
    } else {
      let viewOption = `${view}?${TABLE_URL_PARAMS.SITE_ID}=${siteId}`
      navigationEvent(viewOption, 'REPLACE')
    }
  }

  const navigateToSameTab = url => {
    const type = NavigationHelper.types.OPEN
    navigationEvent(url, type)
  }

  return (
    <AdminDashboardBanner
      availableLinks={getAvailableLinks()}
      displayTitle={displayTitle}
      handleKeyPress={handleKeyPress}
      handleTabChange={handleTabChange}
      hsToggleView={hsToggleView}
      isLaunched={isLaunched}
      isSingleK8Site={isSingleK8Site}
      isSmallGroupLessonCompletionPage={isSmallGroupLessonCompletionPage}
      isUserProgressPage={isUserProgressPage}
      k8ToggleView={k8ToggleView}
      mspSites={mspSites}
      navigateToSameTab={navigateToSameTab}
      navigationEvent={navigationEvent}
      onChangeProgram={onChangeProgram}
      onboardingSnapshot={onboardingSnapshot}
      programTitles={programTitles}
      recentLeaderDashboardUrl={recentLeaderDashboardUrl}
      selectedProgram={selectedProgram}
      sites={sitesForSelectedProgram}
      unassignedLicenses={unassignedLicenses}
      updateMSPOptIn={updateMSPOptIn}
    />
  )
}

AdminDashboardBannerContainer.propTypes = {
  activeAdminHsSites: PropTypes.array,
  activeAdminK8Sites: PropTypes.array,
  claims: PropTypes.object,
  displayTitle: PropTypes.string,
  fetchSetupLicensesForUser: PropTypes.func,
  hsToggleView: PropTypes.string,
  isFetchingSetupLicenses: PropTypes.bool,
  isLaunched: PropTypes.bool,
  isSetupAdmin: PropTypes.bool,
  isSingleK8Site: PropTypes.bool,
  k8ToggleView: PropTypes.string,
  mspSites: PropTypes.array,
  navigationEvent: PropTypes.func,
  onChangeProgram: PropTypes.func,
  onboardingSnapshot: PropTypes.array,
  programQuickLinks: PropTypes.array,
  programTitles: PropTypes.array,
  recentLeaderDashboardUrl: PropTypes.string,
  selectedProgram: PropTypes.object,
  setHSToggleView: PropTypes.func,
  setK8ToggleView: PropTypes.func,
  setLeaderDashboardRecentUrl: PropTypes.func,
  siteId: PropTypes.string,
  unassignedLicenses: PropTypes.array,
  updateMSPOptIn: PropTypes.func,
}

const mapStateToProps = state => {
  const {
    reportsManager,
    userAccessManager,
    licenseManager,
    userSessionManager: {
      adminDashboard: {
        hs: { toggleView: hsToggleView },
        k8: { toggleView: k8ToggleView },
        recentLeaderDashboardUrl,
      },
    },
    userProfileManager,
  } = state
  const { selectedProgram, programTitles, programs } = reportsManager || {}
  const { claims } = userAccessManager || {}
  const { unassignedLicenses, isFetchingSetupLicenses, sites } =
    licenseManager || {}
  const {
    profile: { roles },
  } = userProfileManager || {}
  const isSetupAdmin = roles?.includes(ROLES.SETUP_ADMIN)
  const unassignedSetupLicenses = isSetupAdmin ? unassignedLicenses : []
  const activeAdminHsSites = licenseSelectors.selectActiveAdminHsSites(state)
  const activeAdminK8Sites = licenseSelectors.selectActiveAdminK8Sites(state)
  const mspSites = getMSOptInData(sites)
  const isSingleK8Site = activeAdminK8Sites?.length === 1

  return {
    activeAdminHsSites,
    activeAdminK8Sites,
    claims,
    hsToggleView,
    isFetchingSetupLicenses,
    isSetupAdmin,
    isSingleK8Site,
    k8ToggleView,
    mspSites,
    programs,
    programTitles,
    recentLeaderDashboardUrl,
    selectedProgram,
    unassignedLicenses: unassignedSetupLicenses,
  }
}

const mapDispatchToProps = {
  fetchSetupLicensesForUser: licenseOperations.fetchSetupLicensesForUser,
  navigationEvent,
  setHSToggleView: userSessionOperations.adminSetHSToggleView,
  setK8ToggleView: userSessionOperations.adminSetK8ToggleView,
  setLeaderDashboardRecentUrl:
    userSessionOperations.setLeaderDashboardRecentUrl,
  updateMSPOptIn: siteOperations.updateMSPOptIn,
}

const mapper = entry => {
  const { displayTitle, programQuickLinks } = entry || {}
  return {
    displayTitle,
    programQuickLinks,
  }
}

const options = {
  entryId: '5oqdbnI289AADFVmm2sJyx',
  include: 3,
  mapper,
  spread: true,
}

export default ContentEntryDataGetter(
  withApiError(AdminDashboardBannerContainer, [ERROR_TYPE_CONTENTFUL]),
  options,
  connect(mapStateToProps, mapDispatchToProps),
)
